import { DatastoresTracking } from "@/lib/tracking/datastores";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { FormProgress } from "../../../../components/formProgress/FormProgress";
import DatastoreTypeSelection from "./DatastoreTypeSelection";
import {
  AddDatastoreRequest,
  DatastoreType,
  DatastoreTypeEnum,
  DbtCloudConflictError,
  TableauDatastoreRequest,
} from "../../types";
import { DbtCloudSyncSchedule } from "./DatastoreConnections";
import { ScheduleData } from "./DatastoreConnections/DbtCloudSyncSchedule";
import TableauProjectPicker from "./DatastoreConnections/TableauProjectPicker";
import ConflictingProjectsModal from "./DatastoreConnections/ConflictingProjectsModal";
import { Stack, toast, Typography } from "@altimateai/lego";
import { addDatastore, createDbtCloudConnection } from "@/helpers/apis";
import { DbtCloudConnectionData } from "./DatastoreConnections/DbtCloudConnection";
import {
  TypeData,
  getConfigurationPageByType,
  getConnectionPageByType,
} from "./pageRenderers";

const DatastoreAdd = (): JSX.Element => {
  const navigate = useNavigate();
  const [formProgressLabels, setFormProgressLabels] = useState<string[]>(["Type", "Connection"]);
  const [currentFormPage, setCurrentFormPage] = useState(1);
  const [typeData, setTypeData] = useState<Partial<TypeData>>({});
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictError, setConflictError] = useState<DbtCloudConflictError | null>(null);

  const reportCreateFailure = (detail: string) => {
    DatastoresTracking.createFailed(typeData.type || "unknown", detail);
    toast({ variant: "destructive", title: "Error", description: detail });
  };
  const { mutate: createDatastore, isPending: isCreatingDatastore } = useMutation({
    mutationFn: (data: AddDatastoreRequest) => addDatastore(data),
    // Form-create returns a list of per-database results; ``id: null`` =
    // create failed even though HTTP is 2xx. Don't redirect on such cases.
    onSuccess: response => {
      const failure = (Array.isArray(response) ? response : []).find(
        (i: { id?: number | null }) => i?.id == null
      ) as { message?: string } | undefined;
      if (failure) return reportCreateFailure(failure.message || "Failed to create data store.");
      DatastoresTracking.created(typeData.type || "unknown");
      navigate("/settings/datastores");
    },
    onError: (err: unknown) =>
      reportCreateFailure((err as Error)?.message || "Unable to add Datastore!"),
  });

  const { mutate: createDbtCloud, isPending: isCreatingDbtCloud } = useMutation({
    mutationFn: (data: { connection: DbtCloudConnectionData; schedule: ScheduleData }) =>
      createDbtCloudConnection({
        name: typeData.name || "",
        account_id: data.connection.account_id,
        api_token: data.connection.api_token,
        api_base_url: data.connection.api_base_url,
        sync_type: data.schedule.sync_type,
        schedule_cron: data.schedule.schedule_cron,
        lookback_days: 30, // Default lookback period
      }),
    onSuccess: () => {
      toast({ title: "Success", description: "dbt Cloud connection created successfully" });
      navigate("/settings/datastores");
    },
    onError: (err: unknown) => {
      // Check if this is a conflict error with project details
      // The axios interceptor extracts error.response.data.detail directly,
      // so err is already the conflict error object (not wrapped in {detail: ...})
      const errorObj = err as Record<string, unknown>;
      if (
        errorObj &&
        typeof errorObj === "object" &&
        errorObj.error_code === "CONFLICT_ERROR" &&
        Array.isArray(errorObj.conflicting_projects) &&
        typeof errorObj.message === "string"
      ) {
        // This is a conflict error - show the modal instead of a toast
        setConflictError({
          error_code: "CONFLICT_ERROR",
          message: errorObj.message,
          conflicting_projects:
            errorObj.conflicting_projects as DbtCloudConflictError["conflicting_projects"],
        });
        setConflictModalOpen(true);
        return;
      }

      const errorMessage =
        typeof err === "string"
          ? err
          : (err as Error)?.message || "Unable to create dbt Cloud connection!";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    },
  });

  const handleDbtCloudScheduleFinish = (scheduleData: ScheduleData) => {
    const connection = typeData.connection as unknown as DbtCloudConnectionData;
    createDbtCloud({ connection, schedule: scheduleData });
  };

  const handleBack = () => {
    setCurrentFormPage(prevState => {
      if (prevState > 1) {
        return prevState - 1;
      }
      return prevState;
    });
  };

  const handleTypeSubmit = (data: { name: string; type: DatastoreType; description: string }) => {
    setTypeData(data);
    const step3Label: Partial<Record<DatastoreType, string>> = {
      [DatastoreTypeEnum.TABLEAU]: "Create Altimate Workbooks",
      [DatastoreTypeEnum.DBT_CLOUD]: "Schedule",
      [DatastoreTypeEnum.SNOWFLAKE]: "Configuration",
    };
    const third = step3Label[data.type];
    setFormProgressLabels(third ? ["Type", "Connection", third] : ["Type", "Connection"]);
    setCurrentFormPage(2);
  };

  const handleConnectionSubmit = (data: Record<string, unknown>) => {
    setTypeData(prev => ({ ...prev, connection: data }));
    // Tableau Explorer PATs locate existing workbooks (cannot publish).
    if (data?.can_publish_workbooks === false) {
      setFormProgressLabels(prev =>
        prev.length >= 3 ? [prev[0], prev[1], "Locate Altimate Workbooks"] : prev
      );
    }
    setCurrentFormPage(3);
  };

  // ``can_publish_workbooks`` is a frontend-only UI carry; omitted from send.
  const stripTableauUiOnlyKeys = ({
    can_publish_workbooks: _x,
    ...rest
  }: Record<string, unknown> = {}) => rest;
  const handleProjectPickerFinish = (selectedProject: { id: string }) =>
    createDatastore({
      ...stripTableauUiOnlyKeys(typeData.connection),
      worksheet_location: selectedProject.id,
    } as unknown as TableauDatastoreRequest);

  return (
    <Stack className="al-w-full al-items-center al-justify-center">
      <div className="al-min-w-[50vw]">
        <Typography variant="page-heading" className={"al-mb-4"}>
          Create a new connection
        </Typography>
        <FormProgress labels={formProgressLabels} currentPage={currentFormPage} />
        {currentFormPage === 1 && (
          <DatastoreTypeSelection
            typeData={typeData}
            setFormProgressLabels={setFormProgressLabels}
            handleSubmit={handleTypeSubmit}
          />
        )}
        {currentFormPage === 2 &&
          typeData.type &&
          getConnectionPageByType(typeData as TypeData, handleBack, handleConnectionSubmit)}
        {currentFormPage === 3 &&
          typeData.type === DatastoreTypeEnum.TABLEAU &&
          typeData.connection && (
            <TableauProjectPicker
              credentials={{
                url: (typeData.connection.url as string) || "",
                site_name: typeData.connection.site_name as string,
                pat_name: (typeData.connection.pat_name as string) || "",
                pat_secret: (typeData.connection.pat_secret as string) || "",
              }}
              // Defaults to true if the backend didn't surface a role (older backends).
              canPublishWorkbooks={
                (typeData.connection.can_publish_workbooks as boolean | undefined) ?? true
              }
              isFinishing={isCreatingDatastore}
              handleBack={handleBack}
              handleFinish={handleProjectPickerFinish}
            />
          )}
        {currentFormPage === 3 && typeData.type === DatastoreTypeEnum.DBT_CLOUD && (
          <DbtCloudSyncSchedule
            handleBack={handleBack}
            handleFinish={handleDbtCloudScheduleFinish}
            isCreating={isCreatingDbtCloud}
          />
        )}
        {currentFormPage === 3 &&
          typeData.type &&
          typeData.type !== DatastoreTypeEnum.TABLEAU &&
          typeData.type !== DatastoreTypeEnum.DBT_CLOUD &&
          getConfigurationPageByType(typeData as TypeData, handleBack)}
      </div>

      {/* Conflict error modal for dbt Cloud */}
      {conflictError && (
        <ConflictingProjectsModal
          open={conflictModalOpen}
          onOpenChange={setConflictModalOpen}
          conflictingProjects={conflictError.conflicting_projects}
          message={conflictError.message}
        />
      )}
    </Stack>
  );
};

export default DatastoreAdd;
